import { isDefined } from "@clipboard-health/util-ts";
import { useGetWorkplace } from "@src/appV2/Facilities/api/useGetWorkplace";

interface UseWorkplaceBadgeResult {
  /**
   * Whether the workplace has independent contractor badge feature enabled.
   * Returns `false` while loading or if workplaceId is undefined.
   */
  isEnabled: boolean;
  /**
   * Whether the workplace enforces independent contractor badge verification.
   * Returns `false` while loading or if workplaceId is undefined.
   */
  isEnforced: boolean;
  /**
   * Whether the workplace has a badge verification override that enables the redesign flow.
   * Returns `false` while loading or if workplaceId is undefined.
   */
  hasBadgeVerificationOverride: boolean;
  /**
   * Whether the workplace data is currently loading.
   */
  isLoading: boolean;
  /**
   * Error from fetching workplace data, if any.
   */
  error: unknown;
}

/**
 * Hook to check workplace independent contractor badge settings.
 *
 * @param workplaceId - The workplace ID to check. If undefined, returns false for all flags.
 */
export function useWorkplaceBadge(workplaceId: string | undefined): UseWorkplaceBadgeResult {
  const enabled = isDefined(workplaceId);
  const { data: workplace, isLoading, error } = useGetWorkplace({ workplaceId }, { enabled });

  const { independentContractorBadge } = workplace?.data.attributes ?? {};

  const isEnabled = Boolean(independentContractorBadge?.enabled);
  const isEnforced = Boolean(independentContractorBadge?.workplaceBadgeEnforcement?.enabled);
  const hasBadgeVerificationOverride = Boolean(
    independentContractorBadge?.badgeVerificationOverride
  );

  return {
    isEnabled,
    isEnforced,
    hasBadgeVerificationOverride,
    // Only show loading if the query is enabled and the data is still loading
    isLoading: isLoading && enabled,
    error,
  };
}
